/**
 * Runtime configuration. Vite inlines `import.meta.env` at build time; the
 * deploy pipeline supplies these from the CDK stack outputs.
 */
interface AppConfig {
  apiBaseUrl: string;
  wsBaseUrl: string;
  userPoolId: string;
  userPoolClientId: string;
  cognitoDomain: string;
  region: string;
  /**
   * Whether the deployed user pool actually has Google federation. Having a
   * Cognito domain is not enough — without the provider the hosted UI errors.
   */
  googleEnabled: boolean;
  /**
   * Release identity, baked in by scripts/deploy.sh. 'dev' from a local
   * `npm run dev`, which has no release to name — note that a deployed dev
   * *stage* gets the real number, because it goes through deploy.sh too.
   */
  appVersion: string;
  /** ISO 8601 build timestamp, or '' when not built by scripts/deploy.sh. */
  buildTime: string;
  /**
   * The GA4 measurement id, or '' where this stage ships no analytics.
   *
   * Empty is the off switch and the default, so a local `npm run dev`, every
   * test and any stage deployed without the id behave exactly as the product
   * did before analytics existed: no script, no cookie, no banner. Same shape
   * as `googleEnabled` above and for the same reason — a capability the bundle
   * may only use where the deploy supplied what it needs. The id is not a
   * secret; it ships in the bundle by design.
   */
  gaMeasurementId: string;
}

const env = import.meta.env;

export const config: AppConfig = {
  apiBaseUrl: env.VITE_API_BASE_URL ?? 'http://localhost:3000',
  wsBaseUrl: env.VITE_WS_BASE_URL ?? 'ws://localhost:3001',
  userPoolId: env.VITE_USER_POOL_ID ?? '',
  userPoolClientId: env.VITE_USER_POOL_CLIENT_ID ?? '',
  cognitoDomain: env.VITE_COGNITO_DOMAIN ?? '',
  region: env.VITE_AWS_REGION ?? 'us-east-1',
  googleEnabled: env.VITE_GOOGLE_ENABLED === 'true',
  appVersion: env.VITE_APP_VERSION ?? 'dev',
  buildTime: env.VITE_BUILD_TIME ?? '',
  gaMeasurementId: env.VITE_GA_MEASUREMENT_ID ?? '',
};

export const googleSignInUrl = (redirectUri: string): string => {
  const params = new URLSearchParams({
    identity_provider: 'Google',
    client_id: config.userPoolClientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri,
  });
  return `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
};
